import { type ComponentType } from 'react';
import { withSlideIndex } from './components/deck/SlideIndexContext';
import { CoverSlide } from './slides/CoverSlide';
import { ProblemSlide } from './slides/intelligence/ProblemSlide';
import { LlmLimitsOverviewSlide } from './slides/intelligence/LlmLimitsOverviewSlide';
import { PlatformSolutionSlide } from './slides/intelligence/PlatformSolutionSlide';
import { AdvantagesSlide } from './slides/intelligence/AdvantagesSlide';
import { IntelligenceStackSlide } from './slides/intelligence/IntelligenceStackSlide';
import { SystemOfIntelligenceSlide } from './slides/intelligence/SystemOfIntelligenceSlide';
import { ProductExperienceSlide } from './slides/ProductExperienceSlide';
import { WorkflowSlide } from './slides/intelligence/WorkflowSlide';
import { IndustryAssetsSlide } from './slides/intelligence/IndustryAssetsSlide';
import { CuratedDataSlide } from './slides/intelligence/CuratedDataSlide';
import { InstitutionalMemorySlide } from './slides/intelligence/InstitutionalMemorySlide';
import { WhyNowSlide } from './slides/intelligence/WhyNowSlide';
import { VcWedgeSlide } from './slides/intelligence/VcWedgeSlide';
import { MarketsSlide } from './slides/intelligence/MarketsSlide';
import { MarketOpportunitySlide } from './slides/intelligence/MarketOpportunitySlide';
import { BusinessModelSlide } from './slides/BusinessModelSlide';
import { TractionSlide } from './slides/TractionSlide';
import { ValidationSlide } from './slides/ValidationSlide';
import { DataMoatNetworkSlide } from './slides/intelligence/DataMoatNetworkSlide';
import { SonyCaseStudySlide } from './slides/SonyCaseStudySlide';
import { GoToMarketSlide } from './slides/GoToMarketSlide';
import { ArchitecturePositioningSlide } from './slides/intelligence/ArchitecturePositioningSlide';
import { TeamSlide } from './slides/TeamSlide';
import { VisionSlide } from './slides/intelligence/VisionSlide';
import { FundraiseSlide } from './slides/FundraiseSlide';
export type DeckSlide = {
  // Stable id — keep it matching this slide's screen id in canvas.manifest.js.
  id: string;
  // On-slide title — should match the Screen name in canvas.manifest.js.
  title: string;
  component: ComponentType;
  /** Presenter speaker notes. */
  notes?: string;
};
const deck: DeckSlide[] = [{
  id: 'scr_r7fwnk',
  title: 'Making AI More Accurate, Private & Cost-Efficient for Private Markets',
  component: CoverSlide
}, {
  id: 'scr_prb037',
  title: "Private-market teams are adopting AI. General-purpose models weren't designed for institutional diligence.",
  component: ProblemSlide
}, {
  id: 'scr_llms24',
  title: 'Powerful models. Not built for institutional diligence.',
  component: LlmLimitsOverviewSlide
}, {
  id: 'scr_thes23',
  title: 'The AI + Data Infrastructure Layer for Private Markets.',
  component: PlatformSolutionSlide
}, {
  id: 'scr_adv038',
  title: 'More accurate. More private. More cost-efficient.',
  component: AdvantagesSlide
}, {
  id: 'scr_stk031',
  title: 'The DiligenceGPT™ Intelligence Stack',
  component: IntelligenceStackSlide
}, {
  id: 'scr_cop032',
  title: 'Chatbots answer questions. DiligenceGPT™ builds institutional intelligence.',
  component: SystemOfIntelligenceSlide
}, {
  id: 'scr_loop05',
  title: 'The DiligenceGPT™ platform, in production today.',
  component: ProductExperienceSlide
}, {
  id: 'scr_solu04',
  title: 'One intelligence layer across the investment workflow.',
  component: WorkflowSlide
}, {
  id: 'scr_ast029',
  title: "Professionals don't work from prompts. They work from processes.",
  component: IndustryAssetsSlide
}, {
  id: 'scr_cur030',
  title: 'Not more data. The right data for every decision.',
  component: CuratedDataSlide
}, {
  id: 'scr_mem028',
  title: 'Every workflow makes the intelligence layer stronger.',
  component: InstitutionalMemorySlide
}, {
  id: 'scr_whyn02',
  title: "The models are ready. The institutional infrastructure isn't.",
  component: WhyNowSlide
}, {
  id: 'scr_wdg035',
  title: 'Venture capital is the wedge.',
  component: VcWedgeSlide
}, {
  id: 'scr_mkt034',
  title: 'One intelligence engine. Multiple high-value markets.',
  component: MarketsSlide
}, {
  id: 'scr_bmod11',
  title: 'A large, countable initial market — before expansion.',
  component: MarketOpportunitySlide
}, {
  id: 'scr_trac09',
  title: 'A recurring software model with enterprise contracts.',
  component: BusinessModelSlide
}, {
  id: 'scr_edge08',
  title: 'The evidence that this is working.',
  component: TractionSlide
}, {
  id: 'scr_moat39',
  title: 'Data is our Moat',
  component: DataMoatNetworkSlide,
  notes: 'This is an activated collection network, and we are already receiving data. Public directories help us map the ecosystem; direct participation brings in information that sits inside individual communities, companies and customer workflows. We standardize those contributions and track changes over time, building increasingly useful risk benchmarks, valuation comparables and KPI trends. The moat is the combination of permissioned access, consistent records and historical depth.'
}, {
  id: 'scr_moat07',
  title: 'Trusted by leading organisations across the venture ecosystem.',
  component: ValidationSlide
}, {
  id: 'scr_room06',
  title: 'Sony Innovation Fund: a paid enterprise engagement with room to expand.',
  component: SonyCaseStudySlide
}, {
  id: 'scr_posi12',
  title: 'Land with investors. Expand into enterprise.',
  component: GoToMarketSlide
}, {
  id: 'scr_pos033',
  title: 'The differentiation is the architecture — not simply another model.',
  component: ArchitecturePositioningSlide
}, {
  id: 'scr_gtmk13',
  title: 'Built by operators across venture, enterprise & technology.',
  component: TeamSlide
}, {
  id: 'scr_vis036',
  title: 'Building the Intelligence Infrastructure for Private Markets.',
  component: VisionSlide
}, {
  id: 'scr_team14',
  title: 'The Intelligence Layer for Private Markets.',
  component: FundraiseSlide
}];

/** Page numbers follow presentation order automatically. */
export const slides: DeckSlide[] = deck.map((slide, i) => ({
  ...slide,
  component: withSlideIndex(slide.component, i + 1, deck.length)
}));